import { useEffect, useState } from "react";
import { View, Text, TouchableOpacity, Platform } from "react-native";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { apiRequest } from "../app/lib/api";
import type { Dog, Run, DogStats, BreedRanking } from "../app/lib/types";

interface EventPick {
  id: string;
  name: string;
  club: string | null;
  city: string | null;
  state: string | null;
  firstDate: string;
  lastDate: string;
  closingDate: string | null;
  entryUrl: string | null;
  entryFee: string | null;
  miles: number;
}

// "in Australian Cattle Dog" → "in Australian Cattle Dogs". Skips breeds that
// already end in "s" (Belgian Malinois, Maltese). Good enough for AKC's list;
// occasional Latin plurals (Lhasa Apsos) handle themselves either way.
function pluralBreed(breed: string): string {
  return /s$/i.test(breed) ? breed : `${breed}s`;
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatDateRange(first: string, last: string): string {
  const f = new Date(first);
  const l = new Date(last);
  if (f.toDateString() === l.toDateString()) {
    return `${MONTH_SHORT[f.getMonth()]} ${f.getDate()}`;
  }
  if (f.getMonth() === l.getMonth()) {
    return `${MONTH_SHORT[f.getMonth()]} ${f.getDate()}-${l.getDate()}`;
  }
  return `${MONTH_SHORT[f.getMonth()]} ${f.getDate()} - ${MONTH_SHORT[l.getMonth()]} ${l.getDate()}`;
}

interface Props {
  dog: Dog;
  runs: Run[];
  stats: DogStats | null;
  rankings: BreedRanking[];
  hypotheticalRank: { rank: number; total: number } | null;
  /** When set, "your/you're" pronouns are replaced with this dog's name (e.g. on public profile). */
  dogDisplayName?: string | null;
  /** When true, skip the events fetch + suppress the "Run It Here" event recommendations. */
  publicMode?: boolean;
}

// Replace 2nd-person pronouns with the dog's name for public viewing.
function publicizeBody(body: string, name: string): string {
  return body
    .replace(/\bYou're\b/g, `${name} is`)
    .replace(/\byou're\b/g, `${name} is`)
    .replace(/\bYou'll\b/g, `${name} will`)
    .replace(/\byou'll\b/g, `${name} will`)
    .replace(/\bYour\b/g, `${name}'s`)
    .replace(/\byour\b/g, `${name}'s`)
    .replace(/\bYou\b/g, name)
    .replace(/\byou\b/g, name);
}

interface Insight {
  // Body uses **double-asterisk** to mark highlighted (gold) segments
  body: string;
}

const TIME_FROM_MPH = (mph: number) => 204.545 / mph;

function buildPool(p: Props): Insight[] {
  const pool: Insight[] = [];
  const year = new Date().getFullYear();
  const yearRuns = p.runs.filter((r) => new Date(r.date).getFullYear() === year);

  if (yearRuns.length < 3) return [];

  const speeds = yearRuns
    .map((r) => parseFloat(r.speedMph))
    .filter((n) => !isNaN(n) && n > 0);
  if (speeds.length < 3) return [];

  const top3 = [...speeds].sort((a, b) => b - a).slice(0, 3);
  const best3Avg = top3.reduce((a, b) => a + b, 0) / top3.length;
  const prSpeed = speeds.reduce((a, b) => Math.max(a, b), 0);
  const prTime = TIME_FROM_MPH(prSpeed);
  const overallAvg = speeds.reduce((a, b) => a + b, 0) / speeds.length;

  const projRank = p.hypotheticalRank?.rank ?? null;
  const totalRanked = p.hypotheticalRank?.total ?? null;
  const breed = p.dog.breed;

  // Rule: #1 in breed
  if (projRank === 1) {
    pool.push({
      body: `You're projected **#1** in ${pluralBreed(breed)} nationally. Your rank only drops if another dog catches your **${best3Avg.toFixed(2)} mph** Best 3 Avg — your own runs can't hurt you. Defend the throne.`,
    });
  }

  // Rule: top 3
  if (projRank && projRank >= 2 && projRank <= 3) {
    pool.push({
      body: `**Top 3** in ${pluralBreed(breed)} — that's elite air. Every clean run only pushes your Best 3 Avg higher. Keep showing up.`,
    });
  }

  // Rule: projected rank with achievable time target (tiered)
  if (projRank && projRank > 3) {
    const tiers = [3, 5, 10, 15, 20, 25, 50];
    for (const tier of tiers) {
      if (tier >= projRank) continue;
      const targetRow = p.rankings.find((r) => r.rank === tier);
      const targetSpeed = (targetRow?.speed as number | null) ?? null;
      if (!targetSpeed || targetSpeed <= 0) continue;
      const requiredTime = 409.09 / (3 * targetSpeed - prSpeed);
      if (!isFinite(requiredTime) || requiredTime <= 0) continue;
      const achievable =
        requiredTime >= prTime - 0.5 || requiredTime >= prTime * 0.95;
      if (!achievable) continue;
      const mphDelta = targetSpeed - best3Avg;
      const timeDelta = requiredTime - prTime;
      const closeness = timeDelta >= 0
        ? `That's only **${(prTime - requiredTime > 0 ? prTime - requiredTime : 0).toFixed(2)} seconds** off your pace — well within reach.`
        : `That's **${Math.abs(timeDelta).toFixed(2)} seconds** faster than your personal best — within reach.`;
      pool.push({
        body: `You're projected **#${projRank}** in ${pluralBreed(breed)}, just **${mphDelta.toFixed(2)} mph** off the **top ${tier}**. Run **${requiredTime.toFixed(2)} seconds** in your next 2 runs and you'll crack it. ${closeness}`,
      });
      break;
    }
  }

  // Rule: recent uptrend / downtrend (last 3 vs overall avg, by date)
  if (yearRuns.length >= 5) {
    const sorted = [...yearRuns].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
    const last3 = sorted
      .slice(-3)
      .map((r) => parseFloat(r.speedMph))
      .filter((n) => !isNaN(n));
    if (last3.length === 3) {
      const last3Avg = last3.reduce((a, b) => a + b, 0) / 3;
      const delta = last3Avg - overallAvg;

      if (delta > 0.3) {
        // Simulate 2 more runs at last-3 pace, recompute Best 3 Avg + projected rank
        const simSpeeds = [...speeds, last3Avg, last3Avg];
        const newTop3 = [...simSpeeds].sort((a, b) => b - a).slice(0, 3);
        const newB3A = newTop3.reduce((a, b) => a + b, 0) / 3;
        const rankedSpeeds = p.rankings
          .filter((r) => r.speed != null && (r.speed as number) > 0)
          .map((r) => r.speed as number);
        const newRank = rankedSpeeds.filter((s) => s > newB3A).length + 1;

        const movedB3A = newB3A > best3Avg + 0.01;
        const movedRank = projRank != null && newRank < projRank;

        if (movedB3A && movedRank) {
          pool.push({
            body: `Your last 3 runs are **${delta.toFixed(2)} mph** faster than your overall average. Two more at this pace lifts your Best 3 Avg to **${newB3A.toFixed(2)} mph** — projected **#${newRank}** in ${pluralBreed(breed)}. This is the window.`,
          });
        } else if (movedB3A) {
          pool.push({
            body: `Your last 3 runs are **${delta.toFixed(2)} mph** faster than your overall average. Two more at this pace lifts your Best 3 Avg to **${newB3A.toFixed(2)} mph**. Rank holds for now — the leaderboard above you is tight.`,
          });
        } else {
          // Pace alone won't move B3A — find single-run targets that DO move rank.
          // New top 3 if one run X > t2 = [t1, max(X,t2_alt), ...]; replacing the 3rd fastest:
          // new B3A = (t1 + t2 + X) / 3  (assuming X > t2_third = top3[2])
          const t1 = top3[0];
          const t2 = top3[1];
          const standardTiers = [3, 5, 10, 15, 20, 25, 50].filter((t) => t < projRank!);

          type TierTarget = { tier: number; requiredTime: number; gap: number };
          let comfortable: TierTarget | null = null;
          let stretch: TierTarget | null = null;

          for (const tier of [...standardTiers].sort((a, b) => b - a)) {
            const tierRow = p.rankings.find((r) => r.rank === tier);
            const tierSpeed = (tierRow?.speed as number | null) ?? null;
            if (!tierSpeed || tierSpeed <= 0) continue;
            const requiredX = (tierSpeed + 0.01) * 3 - t1 - t2;
            if (requiredX <= top3[2]) continue;
            const requiredTime = 204.545 / requiredX;
            if (!isFinite(requiredTime) || requiredTime <= 0) continue;
            const gap = prTime - requiredTime;
            if (gap <= 0.3) {
              comfortable = { tier, requiredTime, gap };
            }
            if (gap <= 0.5) {
              stretch = { tier, requiredTime, gap };
            }
          }

          if (comfortable && stretch && stretch.tier < comfortable.tier) {
            pool.push({
              body: `Your last 3 runs are **${delta.toFixed(2)} mph** faster than your overall average — the consistency is locked in. A single **${comfortable.requiredTime.toFixed(2)}-second** run cracks the **top ${comfortable.tier}**; **${stretch.requiredTime.toFixed(2)}** reaches the **top ${stretch.tier}**. The next gear is right there.`,
            });
          } else if (comfortable) {
            pool.push({
              body: `Your last 3 runs are **${delta.toFixed(2)} mph** faster than your overall average — the consistency is locked in. A single **${comfortable.requiredTime.toFixed(2)}-second** run cracks the **top ${comfortable.tier}** in ${pluralBreed(breed)}. One PR-level day is all it takes.`,
            });
          } else if (stretch) {
            pool.push({
              body: `Your last 3 runs are **${delta.toFixed(2)} mph** faster than your overall average — the consistency is locked in. A **${stretch.requiredTime.toFixed(2)}-second** stretch run cracks the **top ${stretch.tier}** in ${pluralBreed(breed)}. The ceiling is closer than it looks.`,
            });
          } else {
            pool.push({
              body: `Your last 3 runs are **${delta.toFixed(2)} mph** faster than your overall average — the consistency is locked in. Your Best 3 Avg holds at **${best3Avg.toFixed(2)} mph** until you stack a PR-level day. One faster run is all it takes to shift rank.`,
            });
          }
        }
      } else if (delta < -0.3) {
        pool.push({
          body: `Last 3 runs are a touch slower than your norm, but your **${best3Avg.toFixed(2)} mph** Best 3 Avg is untouched. One clean day still moves rank up. Nothing on a bad run undoes your best work.`,
        });
      }
    }
  }

  // Rule: volume milestone (≥10 runs this year)
  if (yearRuns.length >= 10) {
    pool.push({
      body: `**${yearRuns.length} clean runs** into the ${year} season with a **${best3Avg.toFixed(2)} mph** Best 3 Avg. Every event sharpens your projection. Keep stacking — that average only goes up from here.`,
    });
  }

  // Rule: top 50% percentile (when nothing else fit and we know total)
  if (pool.length === 0 && projRank && totalRanked) {
    const pct = Math.round((projRank / totalRanked) * 100);
    if (pct <= 50) {
      pool.push({
        body: `You're in the **top ${pct}%** of ${breed} nationally. Keep stacking — every event sharpens the projection. Rare air for an active competitor.`,
      });
    }
  }

  // Fallback
  if (pool.length === 0) {
    pool.push({
      body: `**${yearRuns.length} runs** into ${year} with a **${best3Avg.toFixed(2)} mph** Best 3 Avg. Every event sharpens your projection. Rankings only move up from your best work — keep going.`,
    });
  }

  return pool;
}

// Daily-stable seed: same insight all day, rotates overnight.
function pickInsight(dogId: string, pool: Insight[]): Insight | null {
  if (pool.length === 0) return null;
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000,
  );
  let hash = 0;
  for (let i = 0; i < dogId.length; i++) {
    hash = (hash * 31 + dogId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash + dayOfYear) % pool.length;
  return pool[idx];
}

// Render body string with **gold-bold** segments
function renderBody(body: string) {
  const parts = body.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <Text key={i} style={{ fontWeight: "800", color: "#F5A623" }}>
          {part.slice(2, -2)}
        </Text>
      );
    }
    return <Text key={i}>{part}</Text>;
  });
}

export function DashInsight(props: Props) {
  const router = useRouter();
  const pool = buildPool(props);
  const insight = pickInsight(props.dog.id, pool);

  // Optional event picks for the "Run It Here" section. Silently empty if
  // the user has no zip, no events match, or the request errors. Skipped
  // entirely on public profile views where the viewer isn't the dog's owner.
  const [picks, setPicks] = useState<EventPick[]>([]);
  useEffect(() => {
    if (props.publicMode) return;
    let cancelled = false;
    apiRequest<EventPick[]>("/events/picks")
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setPicks(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [props.publicMode]);

  if (!insight) return null;
  const displayBody = props.dogDisplayName
    ? publicizeBody(insight.body, props.dogDisplayName)
    : insight.body;

  return (
    <View
      style={{
        backgroundColor: "#1B2A4A",
        borderRadius: 14,
        padding: 18,
        marginBottom: 16,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.18,
        shadowRadius: 10,
        elevation: 4,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Ionicons name="sparkles" size={16} color="#F5A623" />
        <Text
          style={{
            fontSize: 12,
            color: "#F5A623",
            fontWeight: "800",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Dash-Insight
        </Text>
      </View>
      <Text style={{ fontSize: 15, color: "#fff", lineHeight: 22 }}>
        {renderBody(displayBody)}
      </Text>

      {!props.publicMode && picks.length > 0 && (
        <View style={{ marginTop: 16, paddingTop: 14, borderTopWidth: 1, borderTopColor: "rgba(255,255,255,0.12)" }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <Ionicons name="flag" size={13} color="#F5A623" />
            <Text style={{ fontSize: 11, color: "#F5A623", fontWeight: "800", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Run It Here
            </Text>
          </View>
          {picks.map((p, i) => (
            <TouchableOpacity
              key={p.id}
              onPress={() => router.push(`/events/${p.id}` as any)}
              activeOpacity={0.7}
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                paddingVertical: 10,
                borderTopWidth: i === 0 ? 0 : 1,
                borderTopColor: "rgba(255,255,255,0.08)",
              }}
            >
              <View style={{ flex: 1, paddingRight: 10 }}>
                <Text style={{ fontSize: 14, color: "#fff", fontWeight: "700" }} numberOfLines={1}>
                  {p.club || p.name}
                </Text>
                <Text style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }} numberOfLines={1}>
                  {formatDateRange(p.firstDate, p.lastDate)}
                  {p.city ? ` · ${p.city}${p.state ? `, ${p.state}` : ""}` : ""}
                  {" · "}
                  <Text style={{ color: "#F5A623", fontWeight: "700" }}>{p.miles} mi</Text>
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color="#94A3B8" />
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
}
